import { cx } from '@linaria/core';
import { forwardRef, useRef, useState } from 'react';
import { getLabelText } from '@pm2/api';
import { breakpoints } from '@ui/css';
import { Overlay, InfoIcon } from '@ui/kit';
import { pluralize } from '@utils/common';
import { stringifyGradeLevelRange } from '@utils/constants';
import { useMatchBreakpoint, PostloadedImage, useToggleState } from '@utils/react';
import { CardLink } from '../../atoms/card-link.js';
import { CardOverlay } from './atom.card-overlay.js';
import { OverlayLightbox } from './atom.overlay-lightbox.js';
import { wrapperCls, buttonCls, imageWrapperCls, imageCls, textWrapperCls, titleCls, metaListCls, metaItemCls, labelCls, overlayCls, infoCircleCls } from './card.style.js';
import { useHover } from './hook.hover.js';
import { useOverlayWidth } from './hook.overlay-width.js';
import { jsx, jsxs } from 'react/jsx-runtime';

const Card = /*#__PURE__*/forwardRef(function Card(props, ref) {
  const isMobile = useMatchBreakpoint(breakpoints.mobile.andDown());
  return isMobile ? /*#__PURE__*/jsx(MobileCard, {
    ...props,
    ref: ref
  }) : /*#__PURE__*/jsx(DesktopCard, {
    ...props,
    ref: ref
  });
});

/**
 * The desktop card acts as a link in itself and allows displaying the overlay
 * on hover after a slight delay.
 */
const DesktopCard = /*#__PURE__*/forwardRef(function DesktopCard({
  mountIn,
  locationInSlider,
  ...props
}, ref) {
  const wrapperRef = useRef(null);
  const overlayRef = useRef(null);
  const [isHovered, hoverProps] = useHover(props.layout);
  const [hasFocus, setHasFocus] = useState(false);
  useOverlayWidth(wrapperRef, overlayRef);
  return /*#__PURE__*/jsxs("section", {
    ref: wrapperRef,
    "data-layout": props.layout,
    ...hoverProps,
    className: wrapperCls,
    children: [/*#__PURE__*/jsxs(CardLink, {
      className: buttonCls,
      ref: ref,
      hostname: props.card.link.portal.hostname,
      path: props.card.link.path,
      layout: props.layout,
      isFocused: hasFocus,
      onFocus: () => setHasFocus(true),
      onBlur: () => setHasFocus(false),
      children: [/*#__PURE__*/jsx("div", {
        className: imageWrapperCls,
        children: /*#__PURE__*/jsx(PostloadedImage, {
          className: imageCls,
          src: props.card.imageSrc,
          sizes: "350px",
          alt: ""
        })
      }), /*#__PURE__*/jsxs("div", {
        className: textWrapperCls,
        children: [/*#__PURE__*/jsx("h3", {
          className: titleCls,
          children: props.card.title
        }), /*#__PURE__*/jsxs("ul", {
          className: metaListCls,
          children: [!!props.card.meta.courseCount && /*#__PURE__*/jsxs("li", {
            className: metaItemCls,
            children: [props.card.meta.courseCount, " ", pluralize("lektion", props.card.meta.courseCount)]
          }), !!props.card.meta.grades?.length && /*#__PURE__*/jsx("li", {
            className: metaItemCls,
            children: stringifyGradeLevelRange(props.card.meta.grades)
          })]
        })]
      }), props.card.meta.label && /*#__PURE__*/jsx("div", {
        className: labelCls,
        children: getLabelText(props.card.meta.label)
      })]
    }), /*#__PURE__*/jsx(Overlay, {
      active: isHovered,
      className: cx(overlayCls, locationInSlider),
      withArrow: false,
      middlewares: [],
      mountIn: mountIn ?? document.body,
      placement: "top",
      containerRef: wrapperRef,
      children: /*#__PURE__*/jsx(CardOverlay, {
        ...props,
        active: isHovered,
        ref: overlayRef
      })
    })]
  });
});

/**
 * the mobile card acts slightly different than the desktop variant. Clicking
 * on the mobile card opens a lightbox with the card overlay which then allows
 * linking to the relevant href.
 */
const MobileCard = /*#__PURE__*/forwardRef(function MobileCard(props, ref) {
  const wrapperRef = useRef(null);
  const [active, actions] = useToggleState();
  return /*#__PURE__*/jsxs("section", {
    ref: wrapperRef,
    "data-layout": props.layout,
    className: wrapperCls,
    children: [/*#__PURE__*/jsxs("button", {
      ref: ref,
      className: buttonCls,
      onClick: actions.enable,
      "aria-label": "Udvid kort",
      children: [/*#__PURE__*/jsxs("div", {
        className: imageWrapperCls,
        children: [/*#__PURE__*/jsx(PostloadedImage, {
          className: imageCls,
          src: props.card.imageSrc,
          sizes: "350px",
          alt: ""
        }), /*#__PURE__*/jsx("div", {
          className: infoCircleCls,
          children: /*#__PURE__*/jsx(InfoIcon, {})
        })]
      }), /*#__PURE__*/jsxs("div", {
        className: textWrapperCls,
        children: [/*#__PURE__*/jsx("h3", {
          className: titleCls,
          children: props.card.title
        }), /*#__PURE__*/jsxs("ul", {
          className: metaListCls,
          children: [!!props.card.meta.courseCount && /*#__PURE__*/jsxs("li", {
            className: metaItemCls,
            children: [props.card.meta.courseCount, " ", pluralize("lektion", props.card.meta.courseCount)]
          }), /*#__PURE__*/jsx("li", {
            className: metaItemCls,
            children: stringifyGradeLevelRange(props.card.meta.grades)
          })]
        })]
      })]
    }), /*#__PURE__*/jsx(OverlayLightbox, {
      ...props,
      active: active,
      onClose: actions.disable
    })]
  });
});

export { Card, DesktopCard, MobileCard };
//# sourceMappingURL=card.js.map
